import { apiGet, apiPatch, apiPost, apiUpload } from './client';
import type { Conversation, Message } from '../types';

export async function getConversations(): Promise<Conversation[]> {
  return apiGet<Conversation[]>('/conversations');
}

export async function getConversation(id: string): Promise<Conversation> {
  return apiGet<Conversation>(`/conversations/${id}`);
}

export type LeadApplication = {
  available: boolean;
  status: 'READY_FOR_REVIEW' | 'IDENTITY_REVIEW_REQUIRED' | 'REVIEWED' | 'SENT' | null;
  completedAt: string | null;
  sgp: { status: string; externalId?: string; message?: string };
  plan: { label: string | null; sgpPlanId: string | null };
  data: {
    name: string; document: string; phone: string; email: string;
    addressStreet: string; addressNumber: string; addressComplement: string;
    addressDistrict: string; addressCity: string; addressState: string;
    addressZipCode: string; addressReference: string;
  };
};

export async function getLeadApplication(id: string): Promise<LeadApplication> {
  return apiGet<LeadApplication>(`/conversations/${id}/lead-application`);
}

export async function updateLeadApplication(id: string, data: LeadApplication['data']): Promise<LeadApplication> {
  return apiPatch<LeadApplication>(`/conversations/${id}/lead-application`, data);
}

export async function sendLeadApplicationToSgp(id: string, data: {
  dueDateId: string; billingMethodId: string; sellerId?: string; birthDate?: string;
}): Promise<{ success: boolean; application: LeadApplication }> {
  return apiPost(`/conversations/${id}/lead-application/send-to-sgp`, data);
}

export async function assignConversation(id: string): Promise<Conversation> {
  return apiPost<Conversation>(`/conversations/${id}/assign`);
}

export async function transferConversation(id: string, dto: { departmentId: string; reason?: string }): Promise<Conversation> {
  return apiPost<Conversation>(`/conversations/${id}/transfer`, dto);
}

export async function sendMessage(conversationId: string, content: string): Promise<Message> {
  return apiPost<Message>(`/conversations/${conversationId}/messages`, { content });
}

export async function sendMedia(conversationId: string, file: File, caption?: string): Promise<Message> {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);
  return apiUpload<Message>(`/conversations/${conversationId}/media`, formData);
}

export async function sendDocumentUrl(conversationId: string, url: string, options?: { fileName?: string; caption?: string }): Promise<Message> {
  return apiPost<Message>(`/conversations/${conversationId}/document-url`, {
    url,
    fileName: options?.fileName,
    caption: options?.caption,
  });
}

export async function sendTyping(conversationId: string): Promise<void> {
  return apiPost<void>(`/conversations/${conversationId}/typing`);
}

export async function addNote(conversationId: string, content: string): Promise<Message> {
  return apiPost<Message>(`/conversations/${conversationId}/notes`, { content });
}

export async function closeConversation(id: string, reason?: string): Promise<Conversation> {
  return apiPost<Conversation>(`/conversations/${id}/close`, reason ? { reason } : undefined);
}

export function getMessageFileUrl(messageId: string): string {
  return `/api/files/messages/${messageId}`;
}
